import React from "react";
import { Flex, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import { Modal } from "@libertify/ui";
import styled from "styled-components";
import { PdfViewer } from "components/PdfViewer";
import { DocumentViewer } from "components/DocumentViewer";
import { DocumentFile } from "../types";
import { FileIdentity } from "./FileIdentity";

interface DocumentPreviewModalProps {
  /** File to preview, or `null` when the modal is closed. */
  file: DocumentFile | null;
  onClose: () => void;
}

/**
 * Preview dialog for a single document.
 *
 * PDFs render via `PdfViewer` regardless of whether `previewUrl` is a local
 * `blob:` URL (create flow, not uploaded yet) or a persisted asset URL (edit
 * flow) — pdfjs reads either fine. Other formats (doc/ppt) go through
 * `DocumentViewer`, which proxies through Google Docs Viewer and so needs a
 * real, publicly-reachable URL — a `blob:` URL can't work there, since Google
 * has no way to fetch something scoped to this browser tab. That combination
 * (non-PDF + not uploaded yet) falls back to the "preview once created" note.
 */
export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  file,
  onClose,
}) => {
  const t = useTranslate();

  const previewUrl = file?.previewUrl;
  const isPdf = file?.ext === "pdf";
  const isLocalOnly = previewUrl?.startsWith("blob:") ?? false;
  const canPreview = Boolean(previewUrl) && (isPdf || !isLocalOnly);

  return (
    <Modal
      open={file !== null}
      size={canPreview ? "xlarge" : "medium"}
      showFooter={false}
      title={file?.name ?? ""}
      onClose={onClose}
      styles={
        // Zero out the body's own padding: `PdfHighlighter` fills its
        // positioned ancestor's padding box (i.e. right up to the border,
        // ignoring that ancestor's own padding) — nesting more padding
        // wouldn't help, so the 24px inset is reserved explicitly below,
        // on a wrapper that has no padding of its own for the same reason.
        canPreview
          ? { body: { height: "70vh", padding: 0, position: "relative" } }
          : undefined
      }
      destroyOnHidden
    >
      {file && canPreview && previewUrl ? (
        <PreviewFrame>
          {isPdf ? (
            <PdfViewer url={previewUrl} />
          ) : (
            <DocumentViewer url={previewUrl} />
          )}
        </PreviewFrame>
      ) : (
        <>
          {file ? (
            <Flex align="center" gap={14} style={{ marginBottom: 16 }}>
              <FileIdentity file={file} />
            </Flex>
          ) : null}
          <Typography.Paragraph
            type="secondary"
            style={{ margin: 0, lineHeight: 1.45 }}
          >
            {t("createExperience.addOns.documents.previewNote")}
          </Typography.Paragraph>
        </>
      )}
    </Modal>
  );
};

// ─── Styled components ───

/**
 * Reserves the 24px inset the modal body's own padding can't provide here
 * (see the `styles` comment above) — `position: absolute` makes this the
 * containing block for `PdfHighlighter`'s own absolute fill, and since this
 * frame has no padding of its own, that fill lands exactly on its edges.
 */
const PreviewFrame = styled.div`
  position: absolute;
  inset: 24px;
`;
